function CardList({ jobs, onOpen, upsert, sortOption, bulkMode, selectedIds, onToggleSelect, onAdd, onImportCSV, onDownloadTemplate, renderProBadge }) {
  const [statusMenuId, setStatusMenuId] = React.useState(null);
  const [notesEditId, setNotesEditId] = React.useState(null);
  const [notesEditText, setNotesEditText] = React.useState('');
  const [contactEditId, setContactEditId] = React.useState(null);
  const [contactDraft, setContactDraft] = React.useState({ contactName: '', contactEmail: '', contactLinkedIn: '', sentEmail: false, sentEmailDate: '' });

  React.useEffect(() => {
    if (!statusMenuId) return;
    const close = () => setStatusMenuId(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [statusMenuId]);

  React.useEffect(() => {
    if (!notesEditId) return;
    const onKey = (e) => { if (e.key === 'Escape') setNotesEditId(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [notesEditId]);

  React.useEffect(() => {
    if (!contactEditId) return;
    const onKey = (e) => { if (e.key === 'Escape') setContactEditId(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contactEditId]);

  const { key: sortKey, dir: sortDir } = sortFromOption(sortOption);

  const salaryNumber = (value) => {
    return salarySortNumber(value);
  };

  const sortValue = (job) => {
    if (sortKey === 'company') return (job.company || '').toLowerCase();
    if (sortKey === 'title') return (job.title || '').toLowerCase();
    if (sortKey === 'date') return job.date || '';
    if (sortKey === 'status') return statusSortNumber(job.status);
    if (sortKey === 'salary') return salaryNumber(job.salary);
    return '';
  };

  const sortJobs = (items) => {
    return [...items].sort((a, b) => {
      if (sortKey === 'date') {
        return compareJobsByAppliedDate(a, b, sortDir);
      }

      const av = sortValue(a);
      const bv = sortValue(b);

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }

      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  };

  const handleSaveNotes = async () => {
    const job = jobs.find(j => j.id === notesEditId);
    if (!job) { setNotesEditId(null); return; }
    await upsert({ ...job, notes: notesEditText });
    setNotesEditId(null);
  };

  const handleSaveContact = async () => {
    const job = jobs.find(j => j.id === contactEditId);
    if (!job) { setContactEditId(null); return; }
    await upsert({
      ...job,
      contactName: contactDraft.contactName,
      contactEmail: contactDraft.contactEmail,
      contactLinkedIn: contactDraft.contactLinkedIn,
      sentEmail: contactDraft.sentEmail,
      sentEmailDate: contactDraft.sentEmailDate,
    });
    setContactEditId(null);
  };

  const today  = todayISO();
  const groups = {};

  for (const j of jobs) {
    const k = j.date || 'undated';
    if (!groups[k]) groups[k] = [];
    groups[k].push(j);
  }

  const keys = Object.keys(groups).sort((a, b) => (
    sortDir === 'asc' ? a.localeCompare(b) : b.localeCompare(a)
  ));

  const renderCard = (j) => {
    const stale = j.status === 'applied' && daysBetween(j.date, today) >= STALE_DAYS;
    const isSelected = bulkMode && selectedIds.has(j.id);

    return (
      <div
        key={j.id}
        className={`job-card sketch-box${isSelected ? ' bulk-selected' : ''}`}
        onClick={() => bulkMode ? onToggleSelect(j.id) : onOpen(j)}
        style={{ padding: 14 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            {bulkMode && (
              <input
                type="checkbox"
                className="bulk-cb"
                checked={isSelected}
                onChange={() => onToggleSelect(j.id)}
                onClick={e => e.stopPropagation()}
                style={{ flexShrink: 0, alignSelf: 'center' }}
              />
            )}
            <div>
              <div className="co" style={{ fontSize: 15 }}>
                {stale && <span className="stale-dot"></span>}
                {j.favorite && <span className="material-icons favorite-heart">auto_awesome</span>}
                {j.company}
              </div>
              <div className="ti">{j.title}</div>
            </div>
          </div>
          <span className={`pill ${STATUS_MAP[j.status]?.cls || ''}`}>
            {STATUS_MAP[j.status]?.label || j.status}
          </span>
        </div>
        <hr className="sketch-line" style={{ margin: '10px 0' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--ink-soft)' }}>
          {j.salary && <span><svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3}}><circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3v1m0 5v1M5 5.25C5 4.56 5.67 4 6.5 4S8 4.56 8 5.25 7.33 6.5 6.5 6.5 5 7.06 5 7.75 5.67 9 6.5 9 8 8.44 8 7.75"/></svg>{formatSalaryCompact(j.salary) || formatSalaryValue(j.salary)}</span>}
          {(j.contactName || j.contact || j.contactEmail) && <span><svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:3}}><circle cx="6.5" cy="4.5" r="2.5"/><path d="M1 12.5c0-2.485 2.462-4.5 5.5-4.5s5.5 2.015 5.5 4.5"/></svg>{(j.contactName || j.contact) ? (j.contactName || j.contact).split(',')[0] : j.contactEmail}{!!(j.contactName || j.contact) && j.sentEmail && <span className="material-icons sent-email-badge" aria-label="Email sent" data-tooltip={j.sentEmailDate ? `Email sent\n${prettyDate(j.sentEmailDate)}` : 'Email sent'} style={{fontSize:14,color:'var(--accent)',verticalAlign:'middle',marginLeft:4,cursor:'default',userSelect:'none',lineHeight:1,position:'relative'}}>mark_email_read</span>}</span>}
          {j.notes && (
            <span className="card-note-preview">
              "{j.notes}"
            </span>
          )}
        </div>

        <div className="listing-action" style={{ display: 'flex', alignItems: 'center' }}>
          {safeHref(j.link) && (
            <a
              className="candy-btn link-pink"
              href={safeHref(j.link)}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              View listing
            </a>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }} onMouseDown={e => e.stopPropagation()}>
            <button
              className="candy-btn status-change-btn mob-card-note-btn"
              type="button"
              style={{ width: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'stretch' }}
              onClick={(e) => { e.stopPropagation(); setContactEditId(j.id); setContactDraft({ contactName: j.contactName || j.contact || '', contactEmail: j.contactEmail || '', contactLinkedIn: j.contactLinkedIn || '', sentEmail: j.sentEmail || false, sentEmailDate: j.sentEmailDate || '' }); }}
              title="Edit contact"
            >
              <span className="material-icons" style={{ fontSize: 17 }}>email</span>
            </button>
            <button
              className="candy-btn status-change-btn mob-card-note-btn"
              type="button"
              style={{ width: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'stretch' }}
              onClick={(e) => { e.stopPropagation(); setNotesEditId(j.id); setNotesEditText(j.notes || ''); }}
              title="Edit notes"
            >
              <span className="material-icons" style={{ fontSize: 17 }}>note_add</span>
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="candy-btn status-change-btn"
                type="button"
                onClick={(e) => { e.stopPropagation(); setStatusMenuId(statusMenuId === j.id ? null : j.id); }}
              >
                Change status
              </button>
              {statusMenuId === j.id && (
                <div className="csv-dropdown" style={{ top: 'auto', bottom: 'calc(100% + 4px)', right: 0 }}>
                  {STATUSES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (s.id !== j.status) upsert({ ...j, status: s.id }); setStatusMenuId(null); }}
                      style={s.id === j.status ? { color: 'var(--ink-soft)', cursor: 'default' } : {}}
                    >
                      <span>{s.label}</span>
                      {s.id === j.status && <span style={{ display: 'block', fontSize: 10, color: 'var(--ink-soft)' }}>Current status</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sortedJobs = sortJobs(jobs);
  const showDateGroups = sortKey === 'date';

  return (
    <div>
      {jobs.length === 0 && (
        <div className="view-empty-state">
          <div className="view-empty-title">No jobs yet</div>
          <div className="view-empty-desc">Start tracking your job search by adding a job manually or importing a spreadsheet.</div>
          <div className="view-empty-actions">
            {onAdd && <button className="candy-btn link-pink" onClick={onAdd}>Add Job</button>}
            {onImportCSV && <button className="sketch-btn" onClick={onImportCSV}>Import CSV {renderProBadge && renderProBadge()}</button>}
          </div>
          {onDownloadTemplate && (
            <button className="view-empty-link" onClick={onDownloadTemplate}>Download example spreadsheet</button>
          )}
        </div>
      )}

      {showDateGroups ? (
        keys.map(k => (
          <div key={k} className="date-group">
            <h3>
              {k === 'undated' ? 'no date' : prettyDate(k)}
              <span style={{ fontSize: 13, color: 'var(--ink-soft)', marginLeft: 8, fontWeight: 400 }}>
                ({groups[k].length})
              </span>
            </h3>
            <div className="card-list">
              {sortJobs(groups[k]).map(renderCard)}
            </div>
          </div>
        ))
      ) : (
        <div className="card-list">
          {sortedJobs.map(renderCard)}
        </div>
      )}

      {notesEditId && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200 }}
            onClick={() => setNotesEditId(null)}
          />
          <div
            className="sketch-box"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 201,
              width: 'min(480px, calc(100vw - 32px))',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 600, fontSize: 16 }}>Edit Notes</div>
            <textarea
              className="sketch-input"
              value={notesEditText}
              onChange={e => setNotesEditText(e.target.value)}
              placeholder="anything to remember…"
              style={{ minHeight: 140, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="sketch-btn" type="button" onClick={() => setNotesEditId(null)}>Cancel</button>
              <button className="sketch-btn primary" type="button" onClick={handleSaveNotes}>Save</button>
            </div>
          </div>
        </>
      )}

      {contactEditId && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200 }}
            onClick={() => setContactEditId(null)}
          />
          <div
            className="sketch-box"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 201,
              width: 'min(480px, calc(100vw - 32px))',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 600, fontSize: 16 }}>Edit Contact</div>
            <div className="field">
              <label>Contact name</label>
              <input className="sketch-input" value={contactDraft.contactName} onChange={e => setContactDraft(d => ({ ...d, contactName: e.target.value }))} placeholder="Recruiter, hiring manager, or referral name" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="field">
              <label>Contact email</label>
              <input className="sketch-input" type="email" value={contactDraft.contactEmail} onChange={e => setContactDraft(d => ({ ...d, contactEmail: e.target.value }))} placeholder="recruiter@company.com" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="field">
              <label>Contact LinkedIn</label>
              <input className="sketch-input" value={contactDraft.contactLinkedIn} onChange={e => setContactDraft(d => ({ ...d, contactLinkedIn: e.target.value }))} placeholder="linkedin.com/in/…" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="row2">
              <div className="field">
                <label>Sent email?</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 2 }}>
                  <input
                    type="checkbox"
                    checked={contactDraft.sentEmail}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setContactDraft(d => ({ ...d, sentEmail: checked, sentEmailDate: checked ? todayISO() : '' }));
                    }}
                    style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: 14 }}>Email sent</span>
                </label>
              </div>
              <div className="field modal-date-field">
                <label>Sent email date</label>
                <div className="modal-date-clip w-full max-w-full min-w-0 overflow-hidden">
                  <input className="sketch-input modal-date-input w-full max-w-full min-w-0 box-border appearance-none overflow-hidden" type="date" value={contactDraft.sentEmailDate} onChange={e => setContactDraft(d => ({ ...d, sentEmailDate: e.target.value }))} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="sketch-btn" type="button" onClick={() => setContactEditId(null)}>Cancel</button>
              <button className="sketch-btn primary" type="button" onClick={handleSaveContact}>Save</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
